export const FINANCIAL_DATA_CHANGED_EVENT =
  "metajuntos:financial-data-changed";

const LAST_CHANGE_STORAGE_KEY = "metajuntos:financial-experience:last-change";
const VIEW_CONTEXT_PREFIX = "metajuntos:financial-view:";
const CONTEXT_MAX_AGE_MS = 4 * 60 * 60 * 1000;

export type FinancialSurface =
  | "accounts"
  | "cards"
  | "dashboard"
  | "financial"
  | "invoice"
  | "net-worth";

export type FinancialChangeAction =
  | "account-created"
  | "account-updated"
  | "asset-updated"
  | "card-payment"
  | "expense-payment"
  | "invoice-reopened"
  | "liability-updated"
  | "purchase-created"
  | "purchase-refunded"
  | "transaction-created"
  | "transaction-updated"
  | "workspace-updated";

export type FinancialChangeDetail = {
  id: string;
  occurredAt: number;
  source: string;
  action: FinancialChangeAction;
  surfaces: FinancialSurface[];
  amount?: number;
  accountName?: string;
  previousBalance?: number;
  currentBalance?: number;
  remainingAmount?: number;
  releasedLimit?: number;
};

type FinancialChangeInput = Omit<FinancialChangeDetail, "id" | "occurredAt"> & {
  id?: string;
  occurredAt?: number;
};

type StoredChangeMarker = Pick<
  FinancialChangeDetail,
  "id" | "occurredAt" | "source" | "action" | "surfaces"
>;

type StoredViewContext<T> = {
  savedAt: number;
  value: T;
};

function createChangeId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function canUseSessionStorage() {
  return typeof window !== "undefined" && Boolean(window.sessionStorage);
}

function storeChangeMarker(detail: FinancialChangeDetail) {
  if (!canUseSessionStorage()) return;
  const marker: StoredChangeMarker = {
    id: detail.id,
    occurredAt: detail.occurredAt,
    source: detail.source,
    action: detail.action,
    surfaces: detail.surfaces,
  };
  window.sessionStorage.setItem(LAST_CHANGE_STORAGE_KEY, JSON.stringify(marker));
}

export function publishFinancialExperienceUpdate(
  input: FinancialChangeInput
): FinancialChangeDetail | null {
  if (typeof window === "undefined") return null;
  const detail: FinancialChangeDetail = {
    ...input,
    id: input.id ?? createChangeId(),
    occurredAt: input.occurredAt ?? Date.now(),
    surfaces: Array.from(new Set(input.surfaces)),
  };
  storeChangeMarker(detail);
  window.dispatchEvent(
    new CustomEvent<FinancialChangeDetail>(FINANCIAL_DATA_CHANGED_EVENT, {
      detail,
    })
  );
  return detail;
}

export function isFinancialChangeForSurface(
  detail: Partial<FinancialChangeDetail> | undefined,
  surface: FinancialSurface
) {
  return !detail?.surfaces || detail.surfaces.includes(surface);
}

export function readRecentFinancialChange(
  surface: FinancialSurface,
  maxAgeMs = 4_000
): FinancialChangeDetail | null {
  if (!canUseSessionStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(LAST_CHANGE_STORAGE_KEY);
    if (!raw) return null;
    const marker = JSON.parse(raw) as StoredChangeMarker;
    if (
      !marker.id ||
      !Array.isArray(marker.surfaces) ||
      !marker.surfaces.includes(surface) ||
      Date.now() - marker.occurredAt > maxAgeMs
    ) {
      return null;
    }
    return marker;
  } catch {
    return null;
  }
}

export function readFinancialViewContext<T>(key: string, fallback: T): T {
  if (!canUseSessionStorage()) return fallback;
  try {
    const raw = window.sessionStorage.getItem(`${VIEW_CONTEXT_PREFIX}${key}`);
    if (!raw) return fallback;
    const stored = JSON.parse(raw) as StoredViewContext<T>;
    if (Date.now() - stored.savedAt > CONTEXT_MAX_AGE_MS) return fallback;
    return { ...fallback, ...stored.value };
  } catch {
    return fallback;
  }
}

export function saveFinancialViewContext<T>(key: string, value: T) {
  if (!canUseSessionStorage()) return;
  const stored: StoredViewContext<T> = { savedAt: Date.now(), value };
  window.sessionStorage.setItem(
    `${VIEW_CONTEXT_PREFIX}${key}`,
    JSON.stringify(stored)
  );
}

export function getFinancialScrollContainer() {
  if (typeof document === "undefined") return null;
  return document.querySelector<HTMLElement>("[data-app-scroll-container]");
}

export function saveFinancialScrollPosition(key: string) {
  const container = getFinancialScrollContainer();
  const current = readFinancialViewContext(`${key}:scroll`, { scrollTop: 0 });
  saveFinancialViewContext(`${key}:scroll`, {
    ...current,
    scrollTop: container?.scrollTop ?? 0,
  });
}

export function restoreFinancialScrollPosition(key: string) {
  if (typeof window === "undefined") return () => undefined;
  const { scrollTop } = readFinancialViewContext(`${key}:scroll`, {
    scrollTop: 0,
  });
  const frame = window.requestAnimationFrame(() => {
    const container = getFinancialScrollContainer();
    if (container) container.scrollTop = scrollTop;
  });
  return () => window.cancelAnimationFrame(frame);
}

export function buildSafeReturnPath(path: string, fallback: string) {
  if (!path.startsWith("/") || path.startsWith("//")) return fallback;
  return path;
}
